import {observer} from "mobx-react";
import AHref from "../../../components/ahref/ahref";
import Currency from "../../../components/currency/currency";
import FontBody3 from "../../../components/fonts/font-body3/font-body3";
import {FONT_COLOR, FONT_VARIANT} from "../../../components/fonts/font-props";
import IconCoins from "../../../components/icons/icon-coins/icon-coins";
import IconDollarPage from "../../../components/icons/icon-dollar-page/icon-dollar-page";
import IconUser from "../../../components/icons/icon-user/icon-user";
import Icon from "../../../components/icons/icon/icon";
import TextTruncate from "../../../components/text-truncate/text-truncate";
import DataCard from "../../../components/layouts/data-card/data-card";
import DataCardGridLayout from "../../../components/layouts/data-card/data-card-layouts/data-card-grid-layout";
import ExternalLink from "../../../pages/manage-returns/components/external-link/external-link";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import CustomerStore from "../stores/customer-store";
import './customer-data-card.scss'
import RmasLinkableList from "./rmas-linkable-list";
import CustomerCaseStore from "../../manage-returns/rma-page/stores/customer-case-store";
import {Tooltip} from "../../../components/tooltip/tooltip";
import currentPortalStore from "../../../stores/current-portal-store";
import {FEATURES} from "../../../models/Plan";
import {ButtonIcon} from "../../../components/inputs/button-icon/button-icon";
import IconCopy from "../../../components/icons/icon-copy/icon-copy";
import {TOOLTIP_PLACEMENTS} from "../../../components/tooltip/tooltip-box";

interface ICustomerNameProps {
    customerStore: CustomerStore;
    noData: string;
    colorVarName?: string;
    tooltip?: string;
}
const CustomerName: React.FC<ICustomerNameProps> = observer(({ customerStore, noData, colorVarName, tooltip}) => {
    colorVarName = colorVarName ?? 'color-semantic-3';
    return (
        <div className="customer-data-card-customer-name">
            <Tooltip content={tooltip}>
                <Icon Component={IconUser} color={`var(--${colorVarName})`}/>
            </Tooltip>
            {!customerStore.isLoading && <ExternalLink label={customerStore?.fullName ?? noData} url={customerStore?.customerURL} />}
        </div>
    );
});



interface IProps{
    customer?: CustomerStore;
    customerCase?: CustomerCaseStore;
    orderShippingAddress: string;
    conversionRate?: number;
    currencyCode: string;
    hasTopIndicator?: boolean,
    TopIndicator?: React.FC,
    addressId?: string|null,
    isGift?: boolean
}
const CustomerDataCard: React.FC<IProps> = observer(({
    customer,
    customerCase,
    orderShippingAddress,
    conversionRate,
    currencyCode,
    hasTopIndicator,
    TopIndicator,
    addressId,
    isGift
}) => {

    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-create-rma';
    const rmaScoreColorMap : { [key: string]: string } = {
        Green:    'color-green-score',
        Yellow:   'color-yellow-score',
        Red:      'color-red-score',
    }

    if (!customer) {
        return <DataCard hasTopIndicator={hasTopIndicator}><></></DataCard>;
    }
    const scoreColor = currentPortalStore.isFeatureAllowed(FEATURES.BETA_SHOW_RMA_SCORE_ON_RETURNS_PAGE) && customerCase?.rmaScore ?
        rmaScoreColorMap[customerCase?.rmaScore] :
        undefined;
    const scoreReason = scoreColor ? customerCase?.rmaScoreReason ?? getTranslation('NO_DATA', pageScope) : undefined;

    const safeCopyToClipboard = (text: string) => {
        try {
            navigator.clipboard.writeText(text);
        } catch (err) {
            console.error(`Could not copy text:${text}; Clipboard API might be not supported.`, err);
        }
    }

    return (
        <DataCard
            hasTopIndicator={hasTopIndicator}
            TopIndicator={TopIndicator}
            toolbar={{
                content: <>
                            <CustomerName
                                customerStore={customer}
                                noData={getTranslation('NO_DATA', pageScope)}
                                colorVarName={scoreColor}
                                tooltip={scoreReason}
                            />
                         </>
            }}>
                {(customer.isNotFound || customer.isNotExists) && (
                  <div className={'customer-data-unavailable-overlay'}>
                      <div className={'customer-data-unavailable-badge'}>
                          {getTranslation('NO_CUSTOMER_INFORMATION', pageScope)}
                      </div>
                  </div>
                )}
                <div className={'customer-data-card-content'}>
                    <DataCardGridLayout>
                        <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('RMAS', pageScope)} </FontBody3>
                        <FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                            <RmasLinkableList 
                                isLoading={customer.isLoadingRmas || customer.isLoading}
                                rmasList={customer.getRmas()}
                                noData={getTranslation("DATA_VALUE_NOT_APPLICABLE", "dashboard-returns-rma-card")}
                                searchInput={customer.denormalizedId}
                            />
                        </FontBody3>
                        <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('STORE_CREDIT', pageScope)}</FontBody3>
                        <FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                            <span className="return-credits-links">
                                <AHref href={customer?.getBalanceLink() ?? ''} target="_blank">
                                    <Icon Component={IconCoins}></Icon><Currency currencyCode={currencyCode} amount={customer?.getCreditBalance(conversionRate) ?? 0}/></AHref>
                                <AHref href={customer?.getTransactionsLink() ?? ''} target="_blank"><Icon Component={IconDollarPage}></Icon>{getTranslation('TRANSACTIONS', pageScope)}</AHref>
                            </span>
                        </FontBody3>
                        <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('SHIPPING_ADDRESS', pageScope)} </FontBody3>
                        <div className="order-shipping-address-container">
                            <Tooltip
                                placement={TOOLTIP_PLACEMENTS.BOTTOM}
                                content={
                                    <div className="order-shipping-address-tooltip-content">
                                        <FontBody3 color={FONT_COLOR.NEUTRAL_1_WHITE} variant={FONT_VARIANT.DEFAULT}>
                                            {orderShippingAddress ?? getTranslation('NO_DATA', pageScope)}
                                        </FontBody3>
                                    </div>
                                }
                            >
                                <TextTruncate preventDefaultTooltip>
                                    <FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                        {addressId === null && isGift ? (
                                            getTranslation('NOT_REQUIRED', pageScope)
                                        ) : (
                                            orderShippingAddress ?? getTranslation('NO_DATA', pageScope)
                                        )}
                                        </FontBody3>
                                </TextTruncate>
                            </Tooltip>
                            {orderShippingAddress &&
                                <ButtonIcon Component={IconCopy} onClick={() => safeCopyToClipboard(orderShippingAddress)} />
                            }
                        </div>
                        {customerCase?.updatedShippingAddress && (
                            <>
                                <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('UPDATED_ADDRESS', pageScope)}</FontBody3>
                                <div className="updated-shipping-address-container">
                                    <Tooltip
                                        placement={TOOLTIP_PLACEMENTS.BOTTOM}
                                        content={
                                            <div className="updated-shipping-address-tooltip-content">
                                                <FontBody3 color={FONT_COLOR.NEUTRAL_1_WHITE} variant={FONT_VARIANT.DEFAULT}>
                                                    {customerCase.updatedShippingAddress}
                                                </FontBody3>
                                            </div>
                                        }
                                    >
                                        <TextTruncate preventDefaultTooltip>
                                            <FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                                {customerCase.updatedShippingAddress}
                                            </FontBody3>
                                        </TextTruncate>
                                    </Tooltip>
                                    <ButtonIcon Component={IconCopy} onClick={() => safeCopyToClipboard(customerCase.updatedShippingAddress as string)} />
                                </div>
                            </>
                        )}
                    </DataCardGridLayout>
                </div>
        </DataCard>
    );
});

export default CustomerDataCard;
